import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Plus, Search, Edit, Trash2, MoreHorizontal, Package, Loader2 } from 'lucide-react';
import { ProtectedRoute } from '@/components/protected-route';
import { MainLayout } from '@/components/main-layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ServiceForm } from '@/components/services/service-form';
import { ServiceMaterialsForm } from '@/components/services/service-materials-form';
import { useToast } from '@/hooks/use-toast';
import { useServices, useCreateService, useUpdateService, useDeleteService } from '@/hooks/use-services';
import { PageHeader } from '@/components/page-header';
import { ApiService } from '@/types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Info, Settings2 } from 'lucide-react';

export const Route = createFileRoute('/services')({
  component: () => (
    <ProtectedRoute>
      <MainLayout>
        <ServicesPage />
      </MainLayout>
    </ProtectedRoute>
  ),
});

function ServicesPage() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<ApiService | undefined>(undefined);
  
  const { data: services = [], isLoading } = useServices();
  const createService = useCreateService();
  const updateService = useUpdateService();
  const deleteService = useDeleteService();

  const filteredServices = services.filter(
    (service) =>
      service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (service.unit && service.unit.includes(searchQuery.toLowerCase()))
  );

  const handleCreate = async (data: any) => {
    try {
      await createService.mutateAsync({
        name: data.name,
        pricePerUnit: data.price,
        unit: data.unit,
        description: `${data.category} service`,
        isActive: true,
      });
      setIsDialogOpen(false);
      toast({
        title: 'Service Created',
        description: `${data.name} has been added to catalog.`,
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to create service',
        variant: 'destructive',
      });
    }
  };

  const handleUpdate = async (data: any) => {
    if (!editingService) return;
    try {
      await updateService.mutateAsync({
        id: editingService.id,
        data: {
          name: data.name,
          pricePerUnit: data.price,
          unit: data.unit,
          description: `${data.category} service`,
        },
      });
      setIsDialogOpen(false);
      setEditingService(undefined);
      toast({
        title: 'Service Updated',
        description: `${data.name} has been updated successfully.`,
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to update service',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this service?')) {
      try {
        await deleteService.mutateAsync(id);
        toast({
          title: 'Service Deleted',
          description: 'Service has been removed from catalog.',
        });
      } catch (err: any) {
        toast({
          title: 'Error',
          description: err.message || 'Failed to delete service',
          variant: 'destructive',
        });
      }
    }
  };

  const openEditDialog = (service: ApiService) => {
    setEditingService(service);
    setIsDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingService(undefined);
    setIsDialogOpen(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Map API service to form service format
  const mapServiceForForm = (service?: ApiService) => {
    if (!service) return undefined;
    return {
      id: service.id,
      name: service.name,
      price: service.unitPrice || 0,
      unit: service.unit as 'kg' | 'pcs' | 'meter' | 'm2',
      category: (service.description?.includes('wash') ? 'wash' : 
                service.description?.includes('iron') ? 'iron' : 
                service.description?.includes('dry') ? 'dry-clean' : 'special') as 'wash' | 'dry' | 'iron' | 'full' | 'special',
      estimatedHours: 24,
      isActive: service.isActive ?? true,
      createdAt: service.createdAt || new Date().toISOString(),
      updatedAt: service.updatedAt || new Date().toISOString(),
    };
  };

  return (
    <div className="flex flex-col gap-6 min-w-0">
      <PageHeader
        title="Services"
        actions={(
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <Button onClick={openCreateDialog} className="w-full sm:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              Add Service
            </Button>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingService ? 'Edit Service' : 'Add New Service'}
                </DialogTitle>
                <DialogDescription>
                  {editingService
                    ? `Configure ${editingService.name} - ${editingService.unit}`
                    : 'Enter service details to create a new catalog item.'}
                </DialogDescription>
              </DialogHeader>

              {editingService ? (
                <Tabs defaultValue="info" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-4 bg-muted/50 p-1">
                    <TabsTrigger value="info" className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-card shadow-sm transition-all duration-200">
                      <Info className="h-4 w-4" />
                      Basic Info
                    </TabsTrigger>
                    <TabsTrigger value="materials" className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-card shadow-sm transition-all duration-200">
                      <Settings2 className="h-4 w-4" />
                      Materials
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="info" className="mt-0 ring-0 focus-visible:ring-0">
                    <ServiceForm
                      initialData={mapServiceForForm(editingService)}
                      onSubmit={handleUpdate}
                      isLoading={updateService.isPending}
                    />
                  </TabsContent>
                  
                  <TabsContent value="materials" className="mt-0 ring-0 focus-visible:ring-0">
                    <ServiceMaterialsForm 
                      service={editingService} 
                      onSuccess={() => setIsDialogOpen(false)} 
                    />
                  </TabsContent>
                </Tabs>
              ) : (
                <ServiceForm
                  onSubmit={handleCreate}
                  isLoading={createService.isPending}
                />
              )}
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="flex items-center gap-2">
        <div className="relative w-full flex-1 sm:max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search by name or unit..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-md border bg-card py-12 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        </div>
      ) : filteredServices.length === 0 ? (
        <div className="rounded-md border bg-card py-12 text-center text-muted-foreground">
          No services found.
        </div>
      ) : (
        <>
          <div className="space-y-3 md:hidden">
            {filteredServices.map((service) => (
              <article key={service.id} className="rounded-xl border bg-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{service.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{service.description || service.unit}</p>
                  </div>
                  <Badge variant={service.isActive ? 'success' : 'secondary'}>
                    {service.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="mt-2 rounded-md bg-muted/40 px-3 py-2 text-sm">
                  <p className="font-semibold">{formatCurrency(service.unitPrice || 0)}</p>
                  <p className="text-xs text-muted-foreground">Per {service.unit}</p>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button variant="outline" className="gap-2" onClick={() => openEditDialog(service)}>
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    className="gap-2 text-destructive hover:text-destructive"
                    onClick={() => handleDelete(service.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </article>
            ))}
          </div>

          <div className="hidden overflow-x-auto rounded-md border bg-white md:block dark:bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Service</TableHead>
                  <TableHead className="hidden md:table-cell">Unit</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead className="hidden md:table-cell">Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredServices.map((service) => (
                  <TableRow key={service.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 bg-primary/10 rounded-md flex items-center justify-center">
                          <Package className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex flex-col">
                          <span>{service.name}</span>
                          <span className="text-xs text-muted-foreground truncate max-w-[200px]">{service.description || '—'}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge variant="secondary" className="capitalize">
                        {service.unit}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{formatCurrency(service.unitPrice || 0)}</span>
                      <span className="text-xs text-muted-foreground ml-1">/ {service.unit}</span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge variant={service.isActive ? 'success' : 'secondary'}>
                        {service.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => openEditDialog(service)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleDelete(service.id)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
